import {
  Analysis,
  DashboardConfigurationDependencies,
  Granularity,
} from '../../../../dashboard-config/dashboard-config.model';
import { DashboardFilter } from '../../../../filters/dashboard_filter';
import {
  DashboardDistrictSingleSelectFilter,
  DashboardDistrictsMultiSelectFilter,
  DashboardRemoteDistrictSingleSelectFilter,
  DashboardRemoteStateSingleSelectFilter,
  DashboardRemoteSubMarketSingleSelectFilter,
  DashboardStateSingleSelectFilter,
  DashboardStatesMultiSelectFilter,
  DashboardSubMarketSingleSelectFilter,
  DashboardSubMarketsMultiSelectFilter,
  DashboardWzClassesMultiSelectFilter,
  DashboardWzClassSingleSelectFilter,
} from '../../../../filters/dashboard_filter_implementations';

export const RANKING_SHELL_ID = 'ranking' as const;

export type DashboardRankingShell = 'states' | 'wzClassStates' | 'districts' | 'subMarkets' | 'districtSubMarkets' | 'districtWzClasses';

export interface DashboardRankingShellProfile {
  id: typeof RANKING_SHELL_ID;
  granularityId: Granularity;
  analysisId: Analysis;
  chartSyncable: boolean;
  chartHasSlider: boolean;
  nameColWidth: number;
  baseValueColWidth: number;
  /** Chart heading puts the granularity's by-phrase before its scope ('NACH KREISEN … GAMES, BAYERN'). */
  headingByPhraseFirst?: boolean;
  buildGranularityFilters: (
    deps: DashboardConfigurationDependencies,
    /** `defaultState`: state preselected at Kreise (`ProductRules` district selections). */
    options?: { defaultState?: string },
  ) => DashboardFilter<any>[];
  resolveValueColWidth: (methodologyId: unknown, fallback: number) => number;
}

function resolveFixedWidth(
  _methodologyId: unknown,
  fallback: number,
): number {
  return fallback;
}

function resolveSubMarketValueColWidth(
  methodologyId: unknown,
  fallback: number,
): number {
  const byMethodology: Record<string, number> = {
    '1': 285,
    '2': 385,
    '4': 215,
    '5': 200,
    '6': 345,
    '7': 270,
    '8': 200,
    '9': 300,
    '11': 280,
  };
  return byMethodology[String(methodologyId)] ?? fallback;
}

export const dashboardRankingShellProfiles: Record<DashboardRankingShell, DashboardRankingShellProfile> = {
  states: {
    id: RANKING_SHELL_ID,
    granularityId: Granularity.States,
    analysisId: Analysis.Ranking,
    chartSyncable: true,
    chartHasSlider: false,
    nameColWidth: 185,
    baseValueColWidth: 140,
    buildGranularityFilters: (deps) => [
      new DashboardStatesMultiSelectFilter(deps.localDatasetsService, {
        addSumGermany: false,
      }),
      new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {}),
    ],
    resolveValueColWidth: resolveFixedWidth,
  },
  wzClassStates: {
    id: RANKING_SHELL_ID,
    granularityId: Granularity.WzClassStates,
    analysisId: Analysis.Ranking,
    chartSyncable: true,
    chartHasSlider: false,
    nameColWidth: 185,
    baseValueColWidth: 140,
    buildGranularityFilters: (deps) => [
      new DashboardStatesMultiSelectFilter(deps.localDatasetsService, {
        addSumGermany: false,
      }),
      new DashboardRemoteSubMarketSingleSelectFilter(deps.localDatasetsService, {}),
      new DashboardWzClassSingleSelectFilter(deps.localDatasetsService, {}),
    ],
    resolveValueColWidth: resolveFixedWidth,
  },
  districts: {
    id: RANKING_SHELL_ID,
    granularityId: Granularity.Districts,
    analysisId: Analysis.Ranking,
    chartSyncable: true,
    chartHasSlider: true,
    nameColWidth: 220,
    baseValueColWidth: 180,
    headingByPhraseFirst: true,
    buildGranularityFilters: (deps, options) => {
      const stateFilter = options?.defaultState
        ? new DashboardRemoteStateSingleSelectFilter(deps.localDatasetsService, {
            addSumGermany: true,
            defaultValue: options.defaultState,
            ignoreProductSetting: true,
          })
        : new DashboardRemoteStateSingleSelectFilter(deps.localDatasetsService, {
            addSumGermany: true,
          });

      const urlState = deps.location.getState() as any;
      const subMarketIdFromRoute = urlState?.['filterValues']?.['sub_market']
      const subMarketFilter =  subMarketIdFromRoute
        ? new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {
          ignoreProductSetting: true,
          defaultValue: subMarketIdFromRoute
        })
        : new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {})

      return [
        stateFilter,
        new DashboardDistrictsMultiSelectFilter(deps.localDatasetsService, {
          showInFilterComponent: false,
          parentFilterName: 'state',
          allSelectedOnParentChange: true,
          showAllOptionsWhenParentFilterValueIsNull: true,
        }),
        subMarketFilter,
      ];
    },
    resolveValueColWidth: resolveFixedWidth,
  },
  subMarkets: {
    id: RANKING_SHELL_ID,
    granularityId: Granularity.SubMarkets,
    analysisId: Analysis.Ranking,
    chartSyncable: false,
    chartHasSlider: false,
    nameColWidth: 185,
    baseValueColWidth: 260,
    buildGranularityFilters: (deps) => [
      new DashboardStateSingleSelectFilter(deps.localDatasetsService, {
        addSumGermany: true,
      }),
      new DashboardSubMarketsMultiSelectFilter(deps.localDatasetsService, {
        allInitiallySelectedExceptValue: deps.sumSubMarketIds,
        showInFilterComponent: false,
      }),
    ],
    resolveValueColWidth: resolveSubMarketValueColWidth,
  },
  districtSubMarkets: {
    id: RANKING_SHELL_ID,
    granularityId: Granularity.DistrictSubMarkets,
    analysisId: Analysis.Ranking,
    chartSyncable: true,
    chartHasSlider: false,
    nameColWidth: 185,
    baseValueColWidth: 260,
    buildGranularityFilters: (deps) => [
      // `district` is a child of `state`, so the parent filter has to be part of this config.
      // The district-level dataset has no Σ Germany rows, hence `addSumGermany: false`.
      new DashboardRemoteStateSingleSelectFilter(deps.localDatasetsService, {
        addSumGermany: false,
      }),
      new DashboardDistrictSingleSelectFilter(deps.localDatasetsService, {
        autoDisplayFirst: true,
      }),
      new DashboardSubMarketsMultiSelectFilter(deps.localDatasetsService, {
        allInitiallySelectedExceptValue: deps.sumSubMarketIds,
        showInFilterComponent: false,
      }),
    ],
    resolveValueColWidth: resolveSubMarketValueColWidth,
  },
  districtWzClasses: {
    id: RANKING_SHELL_ID,
    granularityId: Granularity.DistrictWzClasses,
    analysisId: Analysis.Ranking,
    chartSyncable: true,
    chartHasSlider: false,
    nameColWidth: 185,
    baseValueColWidth: 260,
    // Same filters as the time-series shell: the WZ classes of one sub-market, ranked within one district.
    buildGranularityFilters: (deps) => [
      new DashboardRemoteDistrictSingleSelectFilter(deps.localDatasetsService, {
        autoDisplayFirst: true,
      }),
      new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {
        excluded: deps.sumSubMarketIds,
      }),
      new DashboardWzClassesMultiSelectFilter(deps.localDatasetsService, {
        allInitiallySelected: true,
      }),
    ],
    resolveValueColWidth: resolveFixedWidth,
  },
};
